import api from './api';

export interface LogData {
    action: string;
    description: string;
    metadata?: Record<string, any>;
    platform?: string;
}

export interface ActivityLog {
    id: number;
    user_id: number;
    action: string;
    description: string;
    ip_address: string;
    platform: string;
    metadata: Record<string, any>;
    log_api_type?: string | null;
    created_at: string;
}

class LoggingService {

    async log(data: LogData & { log_api_type?: string | null }): Promise<void> {
        try{
            await api.post('/activity-logs', { 
                ...data, 
                platform: data.platform || 'web',
                log_api_type: (data as any).log_api_type || null
            });
        }catch(error){
            console.error('Logging error:', error);
        }
    }

    async logLogout(): Promise<void> {
        await this.log({
            action: 'logout',
            description: 'User logged out',
            platform: 'web'
        });
    }

    async logApiKeyCopy(apiKeyId: number, apiKeyTitle: string): Promise<void> {
        await this.log({
            action: 'api_key_copy',
            description: 'API Key copied',
            metadata: {
                api_key_id: apiKeyId,
                api_key_title: apiKeyTitle
            }
        });
    }

    async logPasswordCopy(passwordId: number, passwordTitle: string): Promise<void> {
        await this.log({
            action: 'password_copy',
            description: 'Password copied',
            metadata: {
                password_id: passwordId,
                password_title: passwordTitle
            }
        });
    }

    async logApiKeyCreate(apiKeyId: number, apiKeyTitle: string): Promise<void> {
        await this.log({
            action: 'api_key_create',
            description: 'New API Key created',
            metadata: {
                api_key_id: apiKeyId,
                api_key_title: apiKeyTitle
            }
        });
    }

    async logApiKeyUpdate(apiKeyId: number, apiKeyTitle: string): Promise<void> {
        await this.log({
            action: 'api_key_update',
            description: 'API Key updated',
            metadata: {
                api_key_id: apiKeyId,
                api_key_title: apiKeyTitle
            }
        });
    }

    async logApiKeyDelete(apiKeyId: number, apiKeyTitle: string): Promise<void> {
        await this.log({
            action: 'api_key_delete',
            description: 'API Key deleted',
            metadata: {
                api_key_id: apiKeyId,
                api_key_title: apiKeyTitle
            }
        });
    }

    async logPasswordCreate(passwordId: number, passwordTitle: string): Promise<void> {
        await this.log({
            action: 'password_create',
            description: 'New Password created',
            metadata: {
                password_id: passwordId,
                password_title: passwordTitle
            }
        });
    }

    async logPasswordUpdate(passwordId: number, passwordTitle: string): Promise<void> {
        await this.log({
            action: 'password_update',
            description: 'Password updated',
            metadata: {
                password_id: passwordId,
                password_title: passwordTitle
            }
        });
    }

    async logPasswordDelete(passwordId: number, passwordTitle: string): Promise<void> {
        await this.log({
            action: 'password_delete',
            description: 'Password deleted',
            metadata: {
                password_id: passwordId,
                password_title: passwordTitle
            }
        });
    }

    async logSearch(searchTerm: string, searchType: string): Promise<void> {
        await this.log({
            action: 'search',
            description: 'Search performed',
            metadata: {
                search_term: searchTerm,
                search_type: searchType
            }
        });
    }

    async logPageView(page: string, module?: string | null, pageTitle?: string): Promise<void> {
        const description = pageTitle ? `Page viewed: ${pageTitle}` : 'Page viewed';
        await this.log({
            action: 'page_view',
            description: description,
            metadata: {
                page: page,
                page_title: pageTitle || null,
                log_api_type: module || null
            },
            log_api_type: module || null
        } as any);
    }

    async logCreate(entity: string, entityId: number, entityName: string, module?: string | null): Promise<void> {
        await this.log({
            action: `${entity}_create`,
            description: `${entity} created`,
            metadata: {
                entity_id: entityId,
                entity_name: entityName
            },
            log_api_type: module || null
        } as any);
    }

    async logEdit(entity: string, entityId: number, entityName: string, module?: string | null): Promise<void> {
        await this.log({
            action: `${entity}_edit`,
            description: `${entity} updated`,
            metadata: {
                entity_id: entityId,
                entity_name: entityName
            },
            log_api_type: module || null
        } as any);
    }

    async logDelete(entity: string, entityId: number, entityName: string, module?: string | null): Promise<void> {
        await this.log({
            action: `${entity}_delete`,
            description: `${entity} deleted`,
            metadata: {
                entity_id: entityId,
                entity_name: entityName
            },
            log_api_type: module || null
        } as any);
    }

    async logExport(): Promise<void> {
        await this.log({
            action: 'export',
            description: 'Data exported'
        });
    }

    async logImport(importedCount: number): Promise<void> {
        await this.log({
            action: 'import',
            description: 'Data imported',
            metadata: {
                imported_count: importedCount
            }
        });
    }

    async logSettingsChange(setting: string, oldValue: string, newValue: string): Promise<void> {
        await this.log({
            action: 'settings_change',
            description: 'Setting changed',
            metadata: {
                setting: setting,
                old_value: oldValue,
                new_value: newValue
            }
        });
    }

    async getLogs(limit: number = 100): Promise<ActivityLog[]> {
        try{
            const response = await api.get('/activity-logs', { params: { limit } });
            return response.data.data;
        }catch(error){
            console.error('Error fetching logs:', error);
            return [];
        }
    }

    async getLogStats(): Promise<any> {
        try{
            const response = await api.get('/activity-logs/stats');
            return response.data.data;
        }catch(error){
            console.error('Error fetching log stats:', error);
            return null;
        }
    }
}

export const loggingService = new LoggingService();
export default loggingService;
